import React, {useContext, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import DynamicImage from '../../dynamic-image'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Navigation, Zoom} from 'swiper/modules'
import {ProductImageAltTextContext} from '../../product-view'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {simple_gtm_event} from '../../../custom-api/layers/default'
import {defineMessages, useIntl} from 'react-intl'

const messages = defineMessages({
    openImage: {
        id: 'gallery-swiper.open-image',
        defaultMessage: 'Open image {index} in full screen',
    },
})

const GallerySwiper = ({
    heroImageGroup,
    loadingStrategy = 'lazy',
    openModal,
    selectedIndex,
    setSelectedIndex,
    styles,
    swiperRef,
    variant = 'default',
    setIsModalGallerySwiper,
    onBoundaryNavigate,
    subModelIdentifier,
    isModalOpen = false,
}) => {
    const swiperStyles = useMultiStyleConfig('Swiper')
    const extraSwiperStyles = useMultiStyleConfig('ExtraSwiper', {variant})
    const intl = useIntl()
    const [isZoomed, setIsZoomed] = useState(false)
    const [zoomOrigin, setZoomOrigin] = useState({x: 50, y: 50})
    const lastTouchRef = useRef(null)
    const touchStartRef = useRef(null)
    const touchMovedRef = useRef(false)

    const handleSlideChange = (swiper) => {
        setSelectedIndex(swiper.activeIndex)
        if (isModalOpen && isZoomed) {
            setIsZoomed(false)
            setZoomOrigin({x: 50, y: 50})
            swiper.allowTouchMove = true
        }
    }

    const handleTouchEnd = (swiper) => {
        const diff = swiper.touches?.diff || 0
        if (Math.abs(diff) < swiper.params.threshold) return

        const isAtStart = swiper.activeIndex === 0
        const isAtEnd = swiper.activeIndex === heroImageGroup?.images?.length - 1
        const moveRight = diff < 0

        if ((isAtStart && !moveRight) || (isAtEnd && moveRight)) {
            onBoundaryNavigate?.(moveRight)
        }
    }
    const imageAltText = useContext(ProductImageAltTextContext)

    const {addLayer} = useGoogleTagManager()
    const handleZoomChange = (swiper, scale) => {
        if (isModalOpen && scale <= 1 && !isZoomed) swiper.allowTouchMove = true

        if (scale > 1) {
            addLayer(
                simple_gtm_event.bind(this, {
                    event: 'productZoom',
                }),
            )
        }
    }

    const handleModalZoomToggle = (event) => {
        if (!isModalOpen) return

        event.preventDefault()
        event.stopPropagation()

        const frame = event.currentTarget
        const rect = frame?.getBoundingClientRect?.()
        if (!rect?.width || !rect?.height) return

        if (isZoomed) {
            setIsZoomed(false)
            setZoomOrigin({x: 50, y: 50})
            if (swiperRef?.current) {
                swiperRef.current.allowTouchMove = true
            }
            return
        }

        const touch = event.changedTouches?.[0] || event.touches?.[0]
        const clientX = touch?.clientX ?? event.clientX
        const clientY = touch?.clientY ?? event.clientY
        const x = ((clientX - rect.left) / rect.width) * 100
        const y = ((clientY - rect.top) / rect.height) * 100
        setZoomOrigin({
            x: Math.min(100, Math.max(0, x)),
            y: Math.min(100, Math.max(0, y)),
        })
        setIsZoomed(true)
        if (swiperRef?.current) {
            swiperRef.current.allowTouchMove = false
        }

        addLayer(
            simple_gtm_event.bind(this, {
                event: 'productZoom',
            }),
        )
    }

    const handleModalMouseMove = (event) => {
        if (!isModalOpen || !isZoomed) return

        const frame = event.currentTarget
        const rect = frame?.getBoundingClientRect?.()
        if (!rect?.width || !rect?.height) return

        const touch = event.changedTouches?.[0] || event.touches?.[0]
        const clientX = touch?.clientX ?? event.clientX
        const clientY = touch?.clientY ?? event.clientY
        const x = ((clientX - rect.left) / rect.width) * 100
        const y = ((clientY - rect.top) / rect.height) * 100

        setZoomOrigin({
            x: Math.min(100, Math.max(0, x)),
            y: Math.min(100, Math.max(0, y)),
        })
    }

    const handleModalTouchStart = (event) => {
        const touch = event.touches?.[0]
        if (!touch) return
        touchStartRef.current = {x: touch.clientX, y: touch.clientY}
        touchMovedRef.current = false
    }

    const handleModalTouchMove = (event) => {
        const touch = event.touches?.[0]
        const start = touchStartRef.current
        if (!touch || !start) return

        if (Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > 10) {
            touchMovedRef.current = true
        }

        handleModalMouseMove(event)
    }

    const handleModalTouchEnd = (event) => {
        if (!isModalOpen) return
        const touch = event.changedTouches?.[0]
        touchStartRef.current = null
        if (!touch || touchMovedRef.current) {
            touchMovedRef.current = false
            lastTouchRef.current = null
            return
        }

        const now = Date.now()
        const lastTouch = lastTouchRef.current
        if (
            lastTouch &&
            now - lastTouch.time <= 350 &&
            Math.hypot(touch.clientX - lastTouch.x, touch.clientY - lastTouch.y) <= 32
        ) {
            lastTouchRef.current = null
            handleModalZoomToggle(event)
            return
        }

        lastTouchRef.current = {time: now, x: touch.clientX, y: touch.clientY}
    }

    const shouldUseFillMediaLayout = Boolean(
        heroImageGroup?.images?.some(
            (img) => img?.bucketType === 'features' || img?.bucketType === 'inTheBox',
        ),
    )

    const swiperContainerRef = useRef(null)

    // Flix's native 3D model player injects a focusable iframe/div (sometimes outside this container); keep it out of the tab order
    useEffect(() => {
        const FLIX_SELECTOR = 'iframe[data-dlg360], .userInput[role="img"], model-viewer'

        const disableTabIndex = (el) => {
            if (el.getAttribute('tabindex') !== '-1') {
                el.setAttribute('tabindex', '-1')
            }
        }

        const scanForFlixViewer = (root) => {
            if (root.matches?.(FLIX_SELECTOR)) disableTabIndex(root)
            root.querySelectorAll?.(FLIX_SELECTOR)?.forEach(disableTabIndex)
        }

        scanForFlixViewer(document.body)

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes') {
                    scanForFlixViewer(mutation.target)
                    return
                }
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) return
                    scanForFlixViewer(node)
                })
            })
        })

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['tabindex', 'class'],
        })

        return () => observer.disconnect()
    }, [])

    return (
        <Box
            ref={swiperContainerRef}
            data-testid="image-gallery-swiper"
            __css={swiperStyles}
            {...(isModalOpen ? extraSwiperStyles.container : {})}
            sx={{
                overflow: 'hidden',
                ...(shouldUseFillMediaLayout ? styles.fillMediaSwiperLayout : {}),
            }}
        >
            <Swiper
                modules={[Navigation, Zoom]}
                slidesPerView={1}
                spaceBetween={0}
                slidesOffsetBefore={0}
                slidesOffsetAfter={0}
                roundLengths
                navigation={{
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                }}
                onInit={(swiper) => swiper.slideTo(selectedIndex, 0)}
                onSwiper={(swiper) => {
                    swiperRef.current = swiper
                }}
                onSlideChange={handleSlideChange}
                onTouchEnd={handleTouchEnd}
                zoom={{maxRatio: 2, minRatio: 1, toggle: false, panOnMouseMove: false}}
                onZoomChange={handleZoomChange}
            >
                {heroImageGroup?.images?.map((img, i) => {
                    const shouldFillImageHeight =
                        img?.bucketType === 'features' || img?.bucketType === 'inTheBox'
                    const isActiveSlide = i === selectedIndex

                    return (
                        <SwiperSlide
                            key={i}
                            className={shouldFillImageHeight ? 'gallery-fill-media-slide' : ''}
                            style={shouldFillImageHeight ? {height: '100%'} : undefined}
                        >
                            <Box
                                as={isModalOpen ? 'div' : 'button'}
                                type={isModalOpen ? undefined : 'button'}
                                className="swiper-zoom-container"
                                {...styles.heroImage}
                                sx={
                                    isModalOpen
                                        ? {
                                              ...(shouldFillImageHeight
                                                  ? styles.fillMediaHero
                                                  : {}),
                                              cursor: isZoomed ? 'zoom-out' : 'zoom-in',
                                              touchAction: isZoomed ? 'none' : 'pan-y',
                                              '& img, & picture img': {
                                                  transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                                                  transform: `scale(${isZoomed ? 2 : 1})`,
                                                  transition: 'transform 180ms ease-out',
                                              },
                                          }
                                        : shouldFillImageHeight
                                        ? styles.fillMediaHero
                                        : {}
                                }
                                aria-label={intl.formatMessage(messages.openImage, {index: i + 1})}
                                data-zoomed={isModalOpen ? isZoomed : undefined}
                                data-zoom-origin={
                                    isModalOpen ? `${zoomOrigin.x}% ${zoomOrigin.y}%` : undefined
                                }
                                // the whole hero container is the tab stop for opening the full-screen gallery, not individual slides
                                tabIndex={-1}
                                aria-hidden={!isModalOpen && !isActiveSlide ? true : undefined}
                                {...(!isModalOpen
                                    ? {
                                          onClick: (e) => {
                                              e.stopPropagation()
                                              setIsModalGallerySwiper(true)
                                              openModal(selectedIndex)
                                          },
                                      }
                                    : {
                                          onDoubleClick: handleModalZoomToggle,
                                          onMouseMove: handleModalMouseMove,
                                          onPointerMove: handleModalMouseMove,
                                          onTouchStart: handleModalTouchStart,
                                          onTouchMove: handleModalTouchMove,
                                          onTouchEnd: handleModalTouchEnd,
                                      })}
                                data-testid="hero-image"
                            >
                                <DynamicImage
                                    {...styles.dynamicImage}
                                    src={`${img.disBaseLink || img.link}[?sw={width}&q=60]`}
                                    sx={shouldFillImageHeight ? styles.fillMediaImageWrapper : {}}
                                    imageProps={{
                                        alt: imageAltText ? imageAltText : img.alt,
                                        loading: loadingStrategy,
                                        aspectRatio: styles.dynamicImage?.ratio,
                                        width: '100%',
                                        height: '100%',
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit:
                                            shouldFillImageHeight && !isModalOpen
                                                ? 'cover'
                                                : 'contain',
                                    }}
                                    mixBlendMode={variant === 'inModal' ? 'normal' : 'multiply'}
                                    subModelIdentifier={subModelIdentifier}
                                    isFetchPriority={i === 0}
                                />
                            </Box>
                        </SwiperSlide>
                    )
                })}
            </Swiper>
        </Box>
    )
}

GallerySwiper.propTypes = {
    heroImageGroup: PropTypes.object,
    loadingStrategy: PropTypes.string,
    openModal: PropTypes.func,
    selectedIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    styles: PropTypes.object,
    swiperRef: PropTypes.object,
    variant: PropTypes.string,
    setSelectedIndex: PropTypes.func,
    setIsModalGallerySwiper: PropTypes.func,
    onBoundaryNavigate: PropTypes.func,
    subModelIdentifier: PropTypes.string,
    isModalOpen: PropTypes.bool,
}

export default GallerySwiper
